import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import { HD_MELANCHOLY_MYSTICAL_COLOR } from '@/app/human-design/chart/melancholy/hd-melancholy-card.component';

/**
 * Kaartvolgorde per kolom, 1:1 uit het Figma-design: links de integratie-,
 * centrerend/mystiek- en weten-poorten van de linkerkolom plus de
 * kanaal-kaart 10-34, rechts de twaalf weten-poorten. Een entry met twee
 * poorten rendert de kanaal-variant van de kaart.
 */
const HD_MELANCHOLY_LEFT_COLUMN: readonly (readonly number[])[] = [
  [57], [34], [10], [20], [51], [25], [38], [28], [39], [55], [10, 34],
];

const HD_MELANCHOLY_RIGHT_COLUMN: readonly (readonly number[])[] = [
  [22], [12], [61], [24], [43], [23], [60], [3], [14], [2], [1], [8],
];

/**
 * Legende-entries: kleur uit de circuit-source-of-truth
 * (`HD_BODYGRAPH_CIRCUITS`), labels uit de `human-design/melancholy`-scope.
 * De poster-legende bevatte ook "Tribaal defensief circuit"; die entry heeft
 * op deze sheet geen waarde en is bewust weggelaten.
 */
const HD_MELANCHOLY_LEGEND = [
  { key: 'integration', color: '#00522B' },
  { key: 'knowing', color: '#00C165' },
  { key: 'centering', color: '#00B9CE' },
  { key: 'mystical', color: HD_MELANCHOLY_MYSTICAL_COLOR },
] as const;

interface MelancholyLegendVm {
  readonly key: string;
  readonly color: string;
  readonly title: string;
  readonly keynote: string;
}

/**
 * Pagina met de Thema's van Melancholie cheat sheet, nagebouwd uit het
 * Figma-design "A3 HD - Melancholy" (node 1877-7159, zonder A3-header,
 * mandala en branding-footer). Twee kaartkolommen rond de centrale
 * bodygraph-afbeelding (individuele circuits gemarkeerd) met daaronder de
 * circuit-legende. De kaarten zelf zijn het gedeelde
 * `hd-melancholy-card`-component; de center-kleurvierkantjes van de poster
 * zijn vervangen door de center-mini-iconen.
 *
 * Per-poort-thema's komen uit de `human-design/gate-melancholy`-scope, de
 * pagina- en legende-teksten uit `human-design/melancholy` via
 * {@link HdGateInfoService.cheatSheetTextIn}. Taalwissel via de gedeelde
 * content-taal-picker; nog niet vertaalde EN-velden vallen terug op NL.
 */
@Component({
  selector: 'hd-melancholy-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-melancholy-cheat-sheet-page.component.html',
  styleUrl: './hd-melancholy-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdMelancholyCheatSheetPageComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly pageTitle = computed(() => this.text('pageTitle'));
  protected readonly pageSubtitle = computed(() => this.text('pageSubtitle'));
  protected readonly chartImage = 'assets/human-design/melancholy/melancholy-circuitry.png';
  protected readonly chartAlt = computed(() => this.text('chartAlt'));

  protected readonly leftColumn = HD_MELANCHOLY_LEFT_COLUMN;
  protected readonly rightColumn = HD_MELANCHOLY_RIGHT_COLUMN;

  protected readonly legend = computed<readonly MelancholyLegendVm[]>(() =>
    HD_MELANCHOLY_LEGEND.map(entry => ({
      key: entry.key,
      color: entry.color,
      title: this.text(`legend.${entry.key}.title`),
      keynote: this.text(`legend.${entry.key}.keynote`),
    })),
  );

  constructor() {
    // Beide content-talen eager laden zodat de taalwissel én de NL-fallback
    // meteen correcte tekst opleveren.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  protected print(): void {
    window.print();
  }

  /** `track`-sleutel voor een kaart-entry (één of twee poorten). */
  protected cardKey(gates: readonly number[]): string {
    return gates.join('-');
  }

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetTextIn('melancholy', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('melancholy', key, 'nl');
  }
}
